import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * PDP "order bump" add-ons card (blocks/senselab-addons.liquid).
 *
 * Checking a row does not touch the cart. When the main product is added,
 * product-form.js reads `selectedItems` and folds the checked add-ons into
 * the same /cart/add.js request. Accelerated checkout (Shop Pay etc.)
 * bypasses the cart, so add-ons can't ride along there.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} totalLine - The centered status line below the rows.
 *
 * @extends {Component<Refs>}
 */
export class SenselabAddonsComponent extends Component {
  requiredRefs = ['totalLine'];

  connectedCallback() {
    super.connectedCallback();

    // The card only works coupled to its section's product form; placed
    // anywhere else it would look functional but never add anything.
    const hasProductForm = document.querySelector(
      `product-form-component[data-section-id="${this.dataset.sectionId}"]`
    );
    if (!hasProductForm && !window.Shopify?.designMode) {
      this.hidden = true;
      return;
    }

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    window.addEventListener('pageshow', this.#onPageShow);

    // Sync the total line with any checkbox state restored by the browser.
    this.refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** @returns {HTMLInputElement[]} */
  get #checkedInputs() {
    return Array.from(this.querySelectorAll('input[type="checkbox"][data-variant-id]:checked'));
  }

  /**
   * Checked add-ons as batch add-to-cart items (read by product-form.js).
   *
   * @returns {Array<{variantId: string, quantity: number}>}
   */
  get selectedItems() {
    return this.#checkedInputs.map((input) => ({
      variantId: /** @type {string} */ (input.dataset.variantId),
      quantity: 1,
    }));
  }

  /** Updates the total line to match the checked rows. */
  refresh() {
    const { totalLine } = this.refs;
    const inputs = this.#checkedInputs;

    if (inputs.length === 0) {
      totalLine.textContent = this.dataset.labelEmpty ?? '';
      totalLine.classList.remove('sl-addons__total--active');
      return;
    }

    const cents = inputs.reduce((sum, input) => sum + (Number(input.dataset.price) || 0), 0);
    const money = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: this.dataset.currency || 'BRL',
    }).format(cents / 100);
    const countLabel = inputs.length === 1 ? this.dataset.labelOne : this.dataset.labelMany;

    totalLine.textContent = `+ ${money} — ${inputs.length} ${countLabel}`;
    totalLine.classList.add('sl-addons__total--active');
  }

  /** Unchecks every row and restores the empty-state total line. */
  reset() {
    for (const input of this.#checkedInputs) {
      input.checked = false;
    }
    this.refresh();
  }

  /**
   * Resets the card once its variants were successfully added to the cart.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;

    const selectedIds = new Set(this.selectedItems.map((item) => item.variantId));
    if (selectedIds.size === 0) return;

    // `merchandiseId` is a gid (gid://shopify/ProductVariant/<id>) — compare the trailing id.
    const lines = Array.isArray(event.lines) ? event.lines : [];
    const includesAddon = lines.some((line) =>
      selectedIds.has(String(line.merchandiseId ?? '').split('/').pop() ?? '')
    );
    if (!includesAddon) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.didError) this.reset();
      })
      .catch(() => {});
  };

  /**
   * Re-syncs the total line when the page is restored from the bfcache.
   *
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) this.refresh();
  };
}

if (!customElements.get('senselab-addons-component')) {
  customElements.define('senselab-addons-component', SenselabAddonsComponent);
}
